import React, { useState } from 'react';
import { Users, User, Crown, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { ref, update, remove } from 'firebase/database';
import { database } from '../../firebase/config';

export default function BandMembers({ user, bandId, bandData, setError, showMessage, getBandMembers, isLeader }) {
  const [expanded, setExpanded] = useState(false);

  const removeMember = async (memberUid, memberName) => {
    if (!user || !bandId || !isLeader()) return;
    if (!window.confirm(`Remove ${memberName} from the band?`)) return;
    try {
      await remove(ref(database, `bands/${bandId}/members/${memberUid}`));
      await remove(ref(database, `bandMembers/${memberUid}`));
      showMessage('Member removed successfully');
    } catch (error) {
      setError('Failed to remove member: ' + error.message);
    }
  };

  const transferLeadership = async (newLeaderUid, newLeaderName) => {
    if (!user || !bandId || !isLeader()) return;
    if (!window.confirm(`Transfer band leadership to ${newLeaderName}? You will become a regular member.`)) return;
    try {
      await update(ref(database, `bands/${bandId}/members/${newLeaderUid}`), { role: 'leader' });
      await update(ref(database, `bands/${bandId}/members/${user.uid}`), { role: 'member' });
      showMessage('Leadership transferred successfully');
    } catch (error) {
      setError('Failed to transfer leadership: ' + error.message);
    }
  };

  return (
    <div className="card-3d rounded-card overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-4 sm:px-6 py-4 flex items-center justify-between hover:bg-white/5 transition text-left"
      >
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <Users className="w-5 h-5 text-white" />
          Band Members ({getBandMembers().length})
        </h2>
        {expanded ? <ChevronUp className="w-5 h-5 text-white" /> : <ChevronDown className="w-5 h-5 text-white" />}
      </button>

      {expanded && (
        <div className="border-t border-surface-border-light p-4 sm:p-6">
          <div className="space-y-2">
            {getBandMembers().map(member => (
              <div key={member.uid} className="border border-surface-border-light rounded-card-btn p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  {member.avatar ? (
                    <img
                      src={member.avatar}
                      alt={member.displayName}
                      className="w-10 h-10 rounded-full object-cover border-2 border-brand flex-shrink-0"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center border-2 border-surface-border-light flex-shrink-0">
                      <User className="w-5 h-5 text-text-subtle" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-sm sm:text-base truncate">{member.displayName}</p>
                      {member.role === 'leader' && (
                        <span className="bg-brand text-white text-xs px-2 py-1 rounded-full whitespace-nowrap">Leader</span>
                      )}
                      {member.uid === user.uid && (
                        <span className="bg-white/10 text-white text-xs px-2 py-1 rounded-full">You</span>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-text-subtle truncate">{member.email}</p>
                    <p className="text-xs text-text-subtle">Joined {new Date(member.joinedAt).toLocaleDateString()}</p>
                  </div>
                </div>
                {isLeader() && member.uid !== user.uid && (
                  <div className="flex gap-2">
                    {member.role !== 'leader' && (
                      <button
                        onClick={() => transferLeadership(member.uid, member.displayName)}
                        className="bg-transparent hover:bg-brand transition p-2 rounded-full"
                        title="Transfer leadership"
                      >
                        <Crown className="w-4 h-4 text-yellow-400" />
                      </button>
                    )}
                    <button
                      onClick={() => removeMember(member.uid, member.displayName)}
                      className="bg-transparent hover:bg-red-600 transition p-2 rounded-full"
                      title="Remove member"
                    >
                      <Trash2 className="w-4 h-4 text-text-subtle" />
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
